import {
  BaseElementWidget,
  CustomWidget,
  http,
  SPI,
  useMatched,
  ViewType,
  Widget
} from '@kunlun/dependencies';
import SmartCsChatDialogVue from './SmartCsChatDialog.vue';
import SmartCsFloatButtonVue from './SmartCsFloatButton.vue';
import { createApp, h, ref } from 'vue';

interface ChatMessage {
  id?: number;
  sessionId?: number;
  senderType: string;
  messageContent: string;
  messageType: string;
  sendTime?: string;
}

// 后端为每个工作台/开口建了独立代理 Model，不同模块使用独立 mutation 名称
const CHAT_MUTATION_MAP: Record<string, string> = {
  deliAriesCorePlatform: 'deliAriesPlatformChatInteractionMutation',
  deliAriesCoreSeller: 'deliAriesSellerChatInteractionMutation',
  deliAriesCorePcMall: 'deliAriesPcMallChatInteractionMutation',
  deliAriesCoreDistributorMall: 'deliAriesDistMallChatInteractionMutation',
  deliAriesCoreApp: 'deliAriesAppChatInteractionMutation'
};

// 按当前路由所在工作台动态定位模块与 mutation 名
function resolveChatRoute(): { module: string; mutation: string } | null {
  const matched = useMatched()?.matched;
  const moduleStr = matched?.segmentParams?.page?.module as string | undefined;
  if (!moduleStr) return null;
  const mutation = CHAT_MUTATION_MAP[moduleStr];
  if (!mutation) return null;
  return { module: moduleStr, mutation };
}

/**
 * 智能客服会话框 Widget
 * 包含浮动图标 + 会话框弹窗
 */
@SPI.ClassFactory(
  BaseElementWidget.Token({
    viewType: ViewType.Table,
    widget: 'SmartCsChat'
  })
)
export class SmartCsChatWidget extends CustomWidget {
  @Widget.Reactive()
  protected chatVisible: boolean = false;

  @Widget.Reactive()
  protected messages: ChatMessage[] = [];

  @Widget.Reactive()
  protected loading: boolean = false;

  @Widget.Reactive()
  protected sending: boolean = false;

  @Widget.Reactive()
  protected humanServiceUrl: string = '';

  @Widget.Reactive()
  protected sessionId: number | null = null;

  @Widget.Reactive()
  protected sessionCode: string = '';

  private customerCode: string = '';
  private customerName: string = '';
  private sellerCode: string = '';

  public initialize(props: any) {
    super.initialize(props);
    this.setComponent(SmartCsChatDialogVue);
    return this;
  }

  // ==================== Chat Actions ====================

  /**
   * Toggle chat dialog visibility
   */
  protected toggleChat() {
    this.chatVisible = !this.chatVisible;
    if (this.chatVisible && !this.sessionId) {
      this.startChat();
    }
  }

  /**
   * Start or resume a chat session
   */
  protected async startChat() {
    const route = resolveChatRoute();
    if (!route) return;
    this.loading = true;
    try {
      const gql = `
        mutation {
          ${route.mutation} {
            startChat(data: {
              customerCode: "${this.customerCode}"
              customerName: "${this.customerName}"
              sellerCode: "${this.sellerCode}"
            }) {
              sessionId
              sessionCode
              status
              humanServiceUrl
              historyMessages {
                id
                senderType
                messageContent
                messageType
                sendTime
              }
            }
          }
        }
      `;
      const res = await http.query<any>(route.module, gql);
      const result = res?.data?.[route.mutation]?.startChat;
      if (result) {
        this.sessionId = result.sessionId;
        this.sessionCode = result.sessionCode;
        this.humanServiceUrl = result.humanServiceUrl || '';
        this.messages = result.historyMessages || [];
      }
    } catch (e) {
      console.error('Failed to start chat', e);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Send a message and get reply
   */
  protected async sendMessage(text: string) {
    if (!text.trim() || !this.sessionId) return;
    const route = resolveChatRoute();
    if (!route) return;

    // Add user message immediately for UI responsiveness
    const userMsg: ChatMessage = {
      senderType: 'CUSTOMER',
      messageContent: text,
      messageType: 'TEXT',
      sendTime: new Date().toISOString()
    };
    this.messages = [...this.messages, userMsg];

    this.sending = true;
    try {
      const escapedText = text.replace(/"/g, '\\"').replace(/\n/g, '\\n');
      const gql = `
        mutation {
          ${route.mutation} {
            sendMessage(data: {
              sessionId: ${this.sessionId}
              userMessage: "${escapedText}"
            }) {
              replyMessage
              replyMessageType
              status
              transferredToUrl
            }
          }
        }
      `;
      const res = await http.query<any>(route.module, gql);
      const result = res?.data?.[route.mutation]?.sendMessage;
      if (result && result.replyMessage) {
        const replyMsg: ChatMessage = {
          senderType: result.replyMessageType === 'SYSTEM' || result.replyMessageType === 'TRANSFER_PROMPT' ? 'SYSTEM' : 'AI',
          messageContent: result.replyMessage,
          messageType: result.replyMessageType,
          sendTime: new Date().toISOString()
        };
        this.messages = [...this.messages, replyMsg];

        // If transferred, update the URL
        if (result.transferredToUrl) {
          this.humanServiceUrl = result.transferredToUrl;
        }
      }
    } catch (e) {
      console.error('Failed to send message', e);
    } finally {
      this.sending = false;
    }
  }

  /**
   * Open human customer service URL
   */
  protected openHumanService() {
    if (this.humanServiceUrl) {
      window.open(this.humanServiceUrl, '_blank');
    }
  }

  /**
   * Close chat dialog
   */
  protected closeChat() {
    this.chatVisible = false;
  }
}
